import { ExportImage } from "@pdfking/compatibility"
import type {
  PDFRasterizeFunction,
  PDFRasterizeResult,
  PDFReaderOptions,
  Pixel,
} from "@pdfking/renderer-api"
import type { PdfiumApi, PdfiumTypeMap } from "@pdfking/renderer-wasm-pdfium"
import type { Bytes } from "@pdfking/tfunc"
import { ToBlob } from "@pdfking/tfunc"

import { CloseDocument } from "../functions/CloseDocument"
import { EnsurePage } from "../functions/EnsurePage"
import {
  ReadDocument,
  type ReadDocumentResult,
} from "../functions/ReadDocument"
import { PageRange } from "../utils/PageRange"
import { ProvisionPdfium } from "../utils/ProvisionPdfium"

type PointerBitmap = PdfiumTypeMap["FPDF_BITMAP"]
type PointerPage = PdfiumTypeMap["FPDF_PAGE"]

const renderPage = async (
  api: PdfiumApi,
  reader: ReadDocumentResult,
  page: number,
  {
    scale = 2,
    max_size = 1980,
  }: {
    scale?: number
    max_size?: number
  } = {}
): Promise<PDFRasterizeResult> => {
  await EnsurePage(api, reader, page)
  const { doc_pointer } = reader
  const { call, safeCall, readBytes } = api
  let page_pointer: null | PointerPage = null
  let bitmap_pointer: null | PointerBitmap = null

  try {
    page_pointer = await safeCall("FPDF_LoadPage", doc_pointer, page - 1)

    const width = await call("FPDF_GetPageWidthF", page_pointer)
    const height = await call("FPDF_GetPageHeightF", page_pointer)

    let scaled_width = Math.floor(width * scale)
    let scaled_height = Math.floor(height * scale)

    if (max_size) {
      const max_dimension = Math.max(scaled_width, scaled_height)
      if (max_dimension > max_size) {
        const scale_factor = max_size / max_dimension
        scaled_width = Math.floor(scaled_width * scale_factor)
        scaled_height = Math.floor(scaled_height * scale_factor)
      }
    }

    bitmap_pointer = await safeCall(
      "FPDFBitmap_Create",
      scaled_width,
      scaled_height,
      1
    )

    await call(
      "FPDF_RenderPageBitmap",
      bitmap_pointer,
      page_pointer,
      0,
      0,
      scaled_width,
      scaled_height,
      0,
      16 // FPDF_REVERSE_BYTE_ORDER
    )

    const bufferPtr = await safeCall("FPDFBitmap_GetBuffer", bitmap_pointer)

    const data = readBytes(
      bufferPtr,
      (scaled_width * scaled_height * 4) as Bytes
    )

    return {
      blob: ToBlob(
        [
          await ExportImage({
            width: scaled_width as Pixel,
            height: scaled_height as Pixel,
            data,
          }),
        ],
        "image/png"
      ),
      size: {
        width: scaled_width as Pixel,
        height: scaled_height as Pixel,
      },
    }
  } finally {
    if (bitmap_pointer) await call("FPDFBitmap_Destroy", bitmap_pointer)
    if (page_pointer) await call("FPDF_ClosePage", page_pointer)
  }
}

async function* PDFRasterizeImpl(
  api: PdfiumApi,
  input: PDFReaderOptions,
  rangeFn?: (page_count: number) => Iterable<number>
): AsyncGenerator<PDFRasterizeResult> {
  const reader = await ReadDocument(api, input)

  try {
    const page_count = await api.call("FPDF_GetPageCount", reader.doc_pointer)

    for (const page of PageRange(page_count, rangeFn)) {
      yield await renderPage(api, reader, page)
    }
  } finally {
    await CloseDocument(api, reader)
  }
}

export const PDFRasterize: PDFRasterizeFunction = (input, rangeFn) =>
  ProvisionPdfium(api => PDFRasterizeImpl(api, input, rangeFn))
